import { Badge } from '@/components/ui/badge'
import type { BillingCycle, GenerationRun } from '@riverpark/types'

export const FIELD_CLS =
  'flex h-10 w-full rounded border border-input bg-card px-3.5 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20'

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

export function currentMonthValue() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

export function formatBillingPeriod(period: string) {
  const [year, month] = period.split('-')
  const name = MONTH_NAMES[Number(month) - 1]
  return name ? `${name} ${year}` : period
}

export function formatRunDate(unixSeconds: number | null) {
  if (!unixSeconds) return '—'
  return new Date(unixSeconds * 1000).toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export type CycleStatus = GenerationRun['status'] | 'not_started'

/** A run can finish 'completed' having still skipped plots for lack of a reading — that isn't
 *  the same as every occupied plot being invoiced, so it gets its own "Partial" badge rather than
 *  reading as done. Plots skipped for other reasons (already invoiced, no owner) don't count. */
export function RunStatusBadge({ run }: { run: GenerationRun | null }) {
  if (!run) return <Badge variant="default">Not started</Badge>
  switch (run.status) {
    case 'completed':
      return run.plotsPendingReading > 0 ? (
        <Badge variant="pending">Partial</Badge>
      ) : (
        <Badge variant="paid">Completed</Badge>
      )
    case 'failed':
      return <Badge variant="overdue">Failed</Badge>
    default:
      return <Badge variant="pending">In progress</Badge>
  }
}

export interface Cycle {
  period: string
  /** null until the first generation attempt — the cycle can exist with nothing run yet. */
  latest: GenerationRun | null
  history: GenerationRun[]
}

/** A period belongs to the list if it has a persisted cycle, a generation run, or both — the
 *  latter keeps pre-existing history intact for runs created before billing_cycles existed. */
export function buildCycles(
  cycles: BillingCycle[],
  runs: GenerationRun[],
  utilityTypeId: string,
): Cycle[] {
  const periods = new Set<string>()
  for (const c of cycles) if (c.utilityTypeId === utilityTypeId) periods.add(c.billingPeriod)

  const runsByPeriod = new Map<string, GenerationRun[]>()
  for (const run of runs) {
    if (run.utilityTypeId !== utilityTypeId) continue
    periods.add(run.billingPeriod)
    const list = runsByPeriod.get(run.billingPeriod) ?? []
    list.push(run)
    runsByPeriod.set(run.billingPeriod, list)
  }

  return [...periods]
    .map((period) => {
      const history = [...(runsByPeriod.get(period) ?? [])].sort(
        (a, b) => b.createdAt - a.createdAt,
      )
      return { period, latest: history[0] ?? null, history }
    })
    .sort((a, b) => (a.period < b.period ? 1 : -1))
}
